import { useFormik } from 'formik';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import { isIbanCountry } from '@/modules/common/utils/bank-details.ts';
import {
  IBAN_REGEX,
  SWIFT_BIC_REGEX,
  US_ROUTING_NUMBER_REGEX,
} from '@/modules/common/utils/regex.ts';
import { OnboardingWrapper } from '@/modules/entity/components/OnboardingWrapper.tsx';
import {
  useEntityItemUncachedQuery,
  useUpdateEntityMutation,
} from '@/modules/entity/queries/entity.tsx';
import { OnboardingStepProps } from '@/modules/entity/utils/stepper/roadsTypes.ts';
import { Alert, TextInputField } from 'ui';
import { AlertIcon } from 'ui/icons';

const validationSchema = yup.object({
  bank_name: yup.string().trim().required('This field is required'),
  country: yup.string().trim(),
  bank_account_bsb: yup
    .string()
    .trim()
    .when('country', ([country], schema) => {
      return country === 'AUS' ? schema.required('This field is required') : schema.optional();
    }),
  bank_account_swift: yup
    .string()
    .trim()
    .when('country', ([country], schema) => {
      return country !== 'AUS'
        ? schema
            .required('This field is required')
            .test(
              'is-valid-swift-bic',
              'Invalid SWIFT/BIC. Enter 8 or 11 characters. Example: DEUTDEFF',
              value => value.toLowerCase() === 'unknown' || SWIFT_BIC_REGEX.test(value),
            )
        : schema.optional();
    }),
  bank_account_iban: yup
    .string()
    .trim()
    .when('country', ([country], schema) => {
      return isIbanCountry(country)
        ? schema
            .required('This field is required')
            .test(
              'is-valid-iban',
              'Invalid IBAN. Enter 15 to 34 characters. Example: DE89370400440532013000',
              value => value.toLowerCase() === 'unknown' || IBAN_REGEX.test(value),
            )
        : schema.optional();
    }),
  bank_account_routing_number: yup
    .string()
    .trim()
    .when('country', ([country], schema) => {
      return country === 'USA'
        ? schema
            .required('This field is required')
            .test(
              'is-valid-us-routing-number',
              'Invalid routing number. Enter 9 digits. Example: 021000021',
              value => value.toLowerCase() === 'unknown' || US_ROUTING_NUMBER_REGEX.test(value),
            )
        : schema.optional();
    }),
  bank_account_name: yup.string().trim().required('This field is required'),
  bank_account_number: yup.string().trim().required('This field is required'),
});

const initialValues = {
  bank_name: '',
  country: '',
  bank_account_bsb: '',
  bank_account_swift: '',
  bank_account_iban: '',
  bank_account_routing_number: '',
  bank_account_name: '',
  bank_account_number: '',
};

export const PaymentInstructions = ({ moveForward, allowSkip }: OnboardingStepProps) => {
  const { id: entity_id = 0 } = useParams();
  const { data } = useEntityItemUncachedQuery(+entity_id, entity_id === 0 || isNaN(+entity_id));

  const getInitialValues = () => {
    return {
      ...initialValues,
      ...data?.entity,
    };
  };

  const formik = useFormik({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema,
    initialValues: getInitialValues(),
    onSubmit: async values => {
      if (formik.dirty) {
        const paymentDetails = {
          ...values,
          bank_account_bsb:
            values.country === 'AUS'
              ? values.bank_account_bsb
              : values.bank_account_bsb || 'Unknown',
          bank_account_swift: values.country === 'AUS' ? '' : values.bank_account_swift,
          bank_account_iban: isIbanCountry(values.country) ? values.bank_account_iban : '',
          bank_account_routing_number:
            values.country === 'USA' ? values.bank_account_routing_number : '',
        };

        await updateEntity({
          ...data?.entity,
          ...paymentDetails,
        });
      }
      moveForward && moveForward();
    },
    enableReinitialize: true,
  });

  const { mutateAsync: updateEntity, isLoading } = useUpdateEntityMutation({
    setErrors: formik.setErrors,
  });

  const handleOnSkip = async () => {
    formik.setErrors({});
    const paymentDetails = {
      ...formik.values,
      bank_name: formik.values.bank_name || 'Unknown',
      bank_account_bsb: formik.values.bank_account_bsb || 'Unknown',
      bank_account_swift:
        formik.values.country !== 'AUS' ? formik.values.bank_account_swift || 'Unknown' : '',
      bank_account_iban: isIbanCountry(formik.values.country)
        ? formik.values.bank_account_iban || 'Unknown'
        : '',
      bank_account_routing_number:
        formik.values.country === 'USA'
          ? formik.values.bank_account_routing_number || 'Unknown'
          : '',
      bank_account_name: formik.values.bank_account_name || 'Unknown',
      bank_account_number: formik.values.bank_account_number || 'Unknown',
    };

    await updateEntity({
      ...data?.entity,
      ...paymentDetails,
    });
    moveForward && moveForward();
  };

  return (
    <OnboardingWrapper
      title="Payment instructions"
      onClickContinue={formik.handleSubmit}
      onClickSkip={allowSkip ? handleOnSkip : undefined}
      formik={formik}
      isLoading={isLoading}
    >
      <>
        <div className="w-full flex-col items-start space-y-2 mb-6">
          <p className="text-[16px] leading-[24px]">
            Please provide your bank account details below for all cash payments
          </p>
          <p className="text-[14px] leading-[22px] text-gray-600">
            Payments will only be made electronically to the bank account in your name. We will not
            make any payments into third party bank accounts.
          </p>
          <p className="text-[14px] leading-[22px] text-gray-600">
            Investors who do not provide EFT details will have distribution payments defaulted into
            reinvestment (where eligible).
          </p>
        </div>
        <div className="w-full flex-col items-start space-y-8">
          <Alert
            icon={AlertIcon}
            variant="neutral-2"
            label="Distribution payments will be made by EFT to investors."
          />
          <TextInputField label="Bank name" name="bank_name" />
          {formik.values.country === 'AUS' ? (
            <TextInputField label="BSB" name="bank_account_bsb" placeholder="123-456" />
          ) : (
            <>
              {formik.values.country === 'USA' && (
                <TextInputField
                  label="Routing number"
                  name="bank_account_routing_number"
                  placeholder="021000021"
                  hintLabel="Enter the 9-digit ABA routing number for your US bank account."
                />
              )}
              {isIbanCountry(formik.values.country) && (
                <TextInputField
                  label="IBAN"
                  name="bank_account_iban"
                  placeholder="DE89370400440532013000"
                  hintLabel="Enter your International Bank Account Number without spaces."
                />
              )}
              <TextInputField
                label="SWIFT/BIC"
                name="bank_account_swift"
                placeholder="DEUTDEFF"
                hintLabel="Enter your bank's 8 or 11 character SWIFT/BIC code."
              />
            </>
          )}
          <TextInputField label="account number" name="bank_account_number" />
          <TextInputField label="account name" name="bank_account_name" />
        </div>
      </>
    </OnboardingWrapper>
  );
};
